import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
  useEffect,
} from 'react';
import { Organizations, MappedTemplate } from '../types';
import { Organization, User } from '@app/shared/types';
import { useMinimizedDrawers } from './MinimizedDrawersContext';
import { CREATE_USER_DRAWER_ID, EDIT_ORG_DRAWER_ID } from '../constants';
import { CREATE_ORG_DRAWER_ID } from '../constants';

interface IUserManagementContext {
  isUserEditDrawerOpen: boolean;
  isCreateOrganizationsDrawerOpen: boolean;
  isEditOrganizationsDrawerOpen: boolean;
  isUserCreateDrawerOpen: boolean;
  isConfirmModalOpen: boolean;
  loggedInUserData: User | null;
  currentUser?: string;
  currentUserData?: User;
  currentOrgData?: Organization;
  createUserData?: User;
  createOrgData?: Organization;
  templates: MappedTemplate[];
  organizations?: Organizations;
  handleEditUser: (id: string | number) => void;
  handleEditUserWithData: (userData: User) => void;
  openCreateUserDrawer: (initialData?: User) => void;
  openCreateOrgDrawer: (initialData?: Organization) => void;
  openEditOrgDrawer: (initialData?: Organization) => void;
  setIsUserEditDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setIsCreateOrganizationsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setIsUserCreateDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setIsConfirmModalOpen: Dispatch<SetStateAction<boolean>>;
  setCurrentUser: Dispatch<SetStateAction<string | null>>;
  setCurrentUserData: Dispatch<SetStateAction<User | null>>;
  setLoggedInUserData: Dispatch<SetStateAction<User | null>>;
  setCurrentOrgData: Dispatch<SetStateAction<Organization | null>>;
  setCreateUserData: Dispatch<SetStateAction<User | null>>;
  setCreateOrgData: Dispatch<SetStateAction<Organization | null>>;
  setIsEditOrganizationsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setIsUserCredentialsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  isUserCredentialsDrawerOpen: boolean;
}

const UserManagementContext = createContext<null | IUserManagementContext>(
  null,
);

export const UserManagementContextProvider = ({
  children,
  initState,
}: {
  children: ReactNode;
  initState: {
    templates: MappedTemplate[];
    organizations?: Organizations;
  };
}) => {
  const { templates, organizations } = initState;
  const [isUserEditDrawerOpen, setIsUserEditDrawerOpen] = useState(false);
  const [isCreateOrganizationsDrawerOpen, setIsCreateOrganizationsDrawerOpen] =
    useState(false);
  const [isUserCreateDrawerOpen, setIsUserCreateDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [currentUserData, setCurrentUserData] = useState<any | null>(null);
  const [loggedInUserData, setLoggedInUserData] = useState<User | null>(null);
  const [currentOrgData, setCurrentOrgData] = useState<any | null>(null);
  const [createUserData, setCreateUserData] = useState<any | null>(null);
  const [createOrgData, setCreateOrgData] = useState<any | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditOrganizationsDrawerOpen, setIsEditOrganizationsDrawerOpen] =
    useState(false);
  const [isUserCredentialsDrawerOpen, setIsUserCredentialsDrawerOpen] =
    useState(false);
  const { lastRestoredItem, clearLastRestoredItem } = useMinimizedDrawers();

  useEffect(() => {
    if (lastRestoredItem) {
      switch (lastRestoredItem.id) {
        case CREATE_USER_DRAWER_ID:
          setCreateUserData(lastRestoredItem.data);
          setIsUserCreateDrawerOpen(true);
          break;
        case CREATE_ORG_DRAWER_ID:
          setCreateOrgData(lastRestoredItem.data);
          setIsCreateOrganizationsDrawerOpen(true);
          break;
        case EDIT_ORG_DRAWER_ID:
          setCurrentOrgData(lastRestoredItem.data);
          setIsEditOrganizationsDrawerOpen(true);
          break;
        default:
          setCurrentUserData(lastRestoredItem.data);
          setCurrentUser(lastRestoredItem.data?.id || null);
          setIsUserEditDrawerOpen(true);
          break;
      }
      clearLastRestoredItem();
    }
  }, [lastRestoredItem, clearLastRestoredItem]);

  const handleEditUser = (userId: string | number) => {
    setCurrentUser(userId);
    setCurrentUserData(null);
    setIsUserEditDrawerOpen(true);
  };

  const handleEditUserWithData = (userData: any) => {
    setCurrentUser(userData.id);
    setCurrentUserData(userData);
    setIsUserEditDrawerOpen(true);
  };

  const openCreateUserDrawer = (initialData?: any) => {
    setCreateUserData(initialData || null);
    setIsUserCreateDrawerOpen(true);
  };

  const openEditOrgDrawer = (initialData?: Organization) => {
    setCurrentOrgData(initialData || null);
    setIsEditOrganizationsDrawerOpen(true);
  };

  const openCreateOrgDrawer = (initialData?: any) => {
    setCreateOrgData(initialData || null);
    setIsCreateOrganizationsDrawerOpen(true);
  };

  return (
    <UserManagementContext.Provider
      value={{
        isUserEditDrawerOpen,
        setIsUserEditDrawerOpen,
        isUserCreateDrawerOpen,
        setIsUserCreateDrawerOpen,
        isCreateOrganizationsDrawerOpen,
        setIsCreateOrganizationsDrawerOpen,
        handleEditUser,
        handleEditUserWithData,
        openCreateUserDrawer,
        openCreateOrgDrawer,
        openEditOrgDrawer,
        currentUser,
        currentUserData,
        loggedInUserData,
        currentOrgData,
        createUserData,
        createOrgData,
        setCurrentUser,
        setCurrentUserData,
        setLoggedInUserData,
        setCurrentOrgData,
        setCreateUserData,
        setCreateOrgData,
        templates,
        organizations,
        isConfirmModalOpen,
        setIsConfirmModalOpen,
        isEditOrganizationsDrawerOpen,
        setIsEditOrganizationsDrawerOpen,
        setIsUserCredentialsDrawerOpen,
        isUserCredentialsDrawerOpen,
      }}
    >
      {children}
    </UserManagementContext.Provider>
  );
};

export const useUserManagementContext = () => {
  const context = useContext(UserManagementContext);
  if (!context) {
    throw new Error(
      'useUserManagementContext must be used within a UserManagementContextProvider',
    );
  }
  return context;
};
